/**
 * siteDate — "what calendar day is it at the practice?" as a YYYY-MM-DD string.
 *
 * WHY THIS EXISTS (2026-10-30). Practice Pulse keyed its checkout rows and its report by
 * `moment().format('YYYY-MM-DD')` — the BROWSER's day, not the practice's. A user whose browser is in
 * another zone (or a report date round-tripped through a UTC-midnight string) landed on the wrong
 * day, so a row saved at 9pm Eastern could be filed under tomorrow and never appear in that day's report.
 * The date is kept as a plain `YYYY-MM-DD` STRING end to end; nothing here builds a moment or a Date
 * from it except the date-picker adapters at the bottom, which use LOCAL-midnight Dates on purpose.
 *
 * ⚠️ Keep this file import-free (Intl only — no moment-timezone). The unit tests execute this exact file.
 */

function pad2(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

/** A Date's LOCAL calendar day as YYYY-MM-DD, or null for a missing/invalid Date. */
export function localDateToYmd(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** 'YYYY-MM-DD' -> a Date at LOCAL midnight of that day (what a date picker draws), or null. */
export function ymdToLocalDate(ymd) {
  const m = typeof ymd === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd) : null;
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // new Date(2026, 1, 31) rolls over to March 3 — reject a day that did not survive the round trip
  return localDateToYmd(d) === ymd ? d : null;
}

/**
 * The practice's calendar day for an instant.
 * @param {Date|number|string} now  the instant (default: right now)
 * @param {string} tz  IANA zone, e.g. settings GENERAL_TIMEZONE. Missing or unknown -> the browser's day.
 * @returns {string|null} 'YYYY-MM-DD', or null when `now` is not a usable instant.
 */
export function siteLocalDate(now = new Date(), tz) {
  const d = now instanceof Date ? now : new Date(now);
  if (isNaN(d.getTime())) return null;

  if (typeof tz === 'string' && tz.trim() !== '') {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz.trim(), year: 'numeric', month: '2-digit', day: '2-digit',
      }).formatToParts(d);
      const get = (type) => (parts.find((p) => p.type === type) || {}).value;
      const y = get('year'); const mo = get('month'); const da = get('day');
      if (y && mo && da) return `${y}-${mo}-${da}`;
    } catch (e) {
      // unknown time zone -> fall through to the browser's day
    }
  }
  return localDateToYmd(d);
}
